import {combineLatest, Observable} from 'rxjs';
import {debounceTime, filter, map, shareReplay, switchMap} from 'rxjs/operators';
import {times} from "@/helper/num";
import {calcTotalAssets, mergeAssets} from '@/assets/calc';
import {currenciesCurrencyCache$} from '@/config';
import {fiat$} from '@/state/fiat';
import {indexPricesCache$} from '@/price-index/price-index';
import {contractAccountsSubs$} from '@/contract-accounts/all/contract-accounts-all';
import {contractBotTradeUserIds$, copyTradeUserIds$} from '@/raw/users';
import {contractTotalAssets$} from '@/assets-contract/assets-contract';

import type {AssetEntity} from '@/types';

export const contractAssetsSubs$ = contractAccountsSubs$.pipe(
  map((contractAccountsAll) =>
    [...contractAccountsAll.keys()].reduce((acc, userId) => {
      return acc.set(
        userId,
        (contractAccountsAll.get(userId) || []).map((account) => {
          const valueFactor = ['USDT', 'MUSDT'].indexOf(account.currency) > -1 ? 1e8 : 1;
          return <AssetEntity>{
            currency: account.currency,
            valueEv: times(account.equityEv, valueFactor),
            bonusBalanceEv: times(account.bonusBalanceEv, valueFactor),
          };
        })
      );
    }, new Map<number, AssetEntity[]>())
  ),
  shareReplay(1)
);

export const contractTotalAssetsSubs$ = combineLatest([
  contractAssetsSubs$,
  indexPricesCache$,
  currenciesCurrencyCache$,
  fiat$,
]).pipe(
  debounceTime(500),
  map(([contractAssetsAll, indexPricesCache, currencies, fiat]) => {
    const allAssets = [].concat(...contractAssetsAll.toArray());
    return calcTotalAssets(allAssets, indexPricesCache, currencies, fiat, false, true);
  })
);

export const botTradeAssets$ = assetsByUsers(contractBotTradeUserIds$);
export const copyTradeAssets$ = assetsByUsers(copyTradeUserIds$);

export const contractTotalAssetsAll$ = combineLatest([
  currenciesCurrencyCache$,
  fiat$,
  indexPricesCache$,
  contractTotalAssets$,
  contractTotalAssetsSubs$,
]).pipe(debounceTime(500), map(mergeAssets), filter(Boolean));

function assetsByUsers(ids$: Observable<number[]>) {
  return combineLatest([filterAssets(ids$), indexPricesCache$, currenciesCurrencyCache$, fiat$]).pipe(
    debounceTime(500),
    map(([contractAssetsAll, indexPricesCache, currencies, fiat]) => {
      const allAssets = [].concat(...contractAssetsAll.toArray());
      return calcTotalAssets(allAssets, indexPricesCache, currencies, fiat, false, true);
    })
  );
}

function filterAssets(ids$: Observable<number[]>) {
  return ids$.pipe(
    switchMap((ids) =>
      contractAssetsSubs$.pipe(
        map((accounts) => {
          return ids.reduce((acc, id) => {
            return acc.set(id, accounts.get(id) || []);
          }, new Map<number, AssetEntity[]>());
        })
      )
    )
  );
}
